import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
	AuditEntry,
	MockProfile,
	Occurrence,
	OccurrenceStatus,
} from '@/types/occurrence'
import { PERSONA } from './catalog'
import { blankOccurrence, SEED_OCCURRENCES } from './seed'

interface UpdateOptions {
	diff?: AuditEntry['diff']
	detail?: string
	sensitive?: boolean
}

interface OccurrenceMockState {
	profile: MockProfile
	occurrences: Occurrence[]
	nextNumber: number
	setProfile: (profile: MockProfile) => void
	reset: () => void
	create: (data: Partial<Occurrence>) => string
	/** Aplica um patch e registra auditoria (protótipo da trilha imutável). */
	update: (
		id: string,
		patch: Partial<Occurrence>,
		event: string,
		opts?: UpdateOptions,
	) => void
	/** Registra um evento de auditoria sem alterar dados (ex.: consulta sensível). */
	audit: (id: string, event: string, opts?: UpdateOptions) => void
	transition: (id: string, to: OccurrenceStatus, justification?: string) => void
}

const nowIso = () => new Date().toISOString().slice(0, 16)
const uid = () => Math.random().toString(36).slice(2, 9)

const entry = (
	profile: MockProfile,
	event: string,
	opts: UpdateOptions = {},
): AuditEntry => ({
	id: `au-${uid()}`,
	at: nowIso(),
	by: PERSONA[profile].name,
	profile,
	event,
	...opts,
})

export const useOccurrenceMock = create<OccurrenceMockState>()(
	persist(
		(set, get) => ({
			profile: 'SSMA_MANAGER',
			occurrences: SEED_OCCURRENCES,
			nextNumber: 2111,

			setProfile: (profile) => set({ profile }),

			reset: () =>
				set({
					occurrences: SEED_OCCURRENCES,
					nextNumber: 2111,
					profile: 'SSMA_MANAGER',
				}),

			create: (data) => {
				const { profile, nextNumber } = get()
				const id = `occ-${nextNumber}`
				const occ = blankOccurrence({
					id,
					number: nextNumber,
					createdBy: PERSONA[profile].name,
					createdAt: new Date().toISOString(),
					audit: [entry(profile, 'Rascunho criado')],
					...data,
				})
				set((s) => ({
					occurrences: [occ, ...s.occurrences],
					nextNumber: s.nextNumber + 1,
				}))
				return id
			},

			update: (id, patch, event, opts) =>
				set((s) => ({
					occurrences: s.occurrences.map((o) =>
						o.id === id
							? {
									...o,
									...patch,
									audit: [entry(s.profile, event, opts), ...o.audit],
								}
							: o,
					),
				})),

			audit: (id, event, opts) =>
				set((s) => ({
					occurrences: s.occurrences.map((o) =>
						o.id === id
							? { ...o, audit: [entry(s.profile, event, opts), ...o.audit] }
							: o,
					),
				})),

			transition: (id, to, justification) =>
				set((s) => ({
					occurrences: s.occurrences.map((o) => {
						if (o.id !== id) return o
						const by = PERSONA[s.profile].name
						return {
							...o,
							status: to,
							cancelledReason:
								to === 'CANCELLED' ? justification : o.cancelledReason,
							closure:
								to === 'CLOSED'
									? { ...o.closure, decidedBy: by, decidedAt: nowIso() }
									: o.closure,
							statusHistory: [
								...o.statusHistory,
								{ at: nowIso(), by, from: o.status, to, justification },
							],
							audit: [
								entry(s.profile, `Status: ${o.status} → ${to}`, {
									detail: justification,
								}),
								...o.audit,
							],
						}
					}),
				})),
		}),
		{
			name: 'ssma-occurrences-mock',
			version: 2,
			// v2: perfil Consultor removido; quem o simulava volta para Gestor SSMA
			migrate: (persisted) => {
				const state = persisted as OccurrenceMockState
				const known = Object.keys(PERSONA)
				return {
					...state,
					profile: known.includes(state.profile)
						? state.profile
						: 'SSMA_MANAGER',
				}
			},
		},
	),
)

export const newId = uid
